import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { SectionArticles } from '@/components/site/listing';
import { Cell, GridShell } from '@/components/site/shell';
import { shareTargetQuery } from '@/lib/pwa';
import { seoHead } from '@/lib/seo';
import { loadSearchPage } from '@/server/functions/pages';

const searchSchema = z.object({
    q: z.string().optional(),
    text: z.string().optional(),
    url: z.string().optional(),
});

export const Route = createFileRoute('/search')({
    validateSearch: searchSchema,
    loaderDeps: ({ search }) => ({ q: shareTargetQuery(search) }),
    loader: ({ deps }) => loadSearchPage({ data: { q: deps.q } }),
    head: ({ loaderData }) => {
        const q = loaderData?.q?.trim();
        return seoHead({
            title: q ? `חיפוש: ${q}` : 'חיפוש',
            description: q
                ? `תוצאות חיפוש עבור "${q}" באשקלון ניוז`
                : 'חיפוש כתבות באשקלון ניוז',
            path: q ? `/search?q=${encodeURIComponent(q)}` : '/search',
            noIndex: true,
        });
    },
    component: SearchPage,
});

function SearchPage() {
    const data = Route.useLoaderData();
    const search = Route.useSearch();
    const navigate = useNavigate({ from: '/search' });
    const [draft, setDraft] = useState(shareTargetQuery(search) ?? '');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <GridShell activePath="/search">
            <Cell crosses={false}>
                <div className="relative">
                    <div className="grid-dots" aria-hidden />
                    <div className="relative px-6 py-16 md:py-20">
                        <p className="text-sm text-white/55">חיפוש</p>
                        <h1 className="mt-4 max-w-3xl text-4xl font-normal tracking-[-0.04em] md:text-5xl">
                            מצאו כתבות
                        </h1>
                        <form
                            className="mt-8 flex max-w-xl items-stretch border border-white/15"
                            onSubmit={(event) => {
                                event.preventDefault();
                                const next = draft.trim();
                                void navigate({
                                    search: next ? { q: next } : {},
                                });
                            }}
                        >
                            <label className="sr-only" htmlFor="site-search-q">
                                חיפוש כתבות
                            </label>
                            <input
                                ref={inputRef}
                                id="site-search-q"
                                name="q"
                                value={draft}
                                onChange={(event) => setDraft(event.target.value)}
                                placeholder="מה מעניין אתכם?"
                                className="min-w-0 flex-1 bg-transparent px-4 py-3 text-base text-white outline-none placeholder:text-white/55"
                                autoComplete="off"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="inline-flex items-center gap-2 border-s border-white/15 px-4 text-sm text-white transition-colors hover:bg-white/[0.06]"
                            >
                                <Search className="size-4" aria-hidden />
                                חיפוש
                            </button>
                        </form>
                        {!data.q ? (
                            <p className="mt-6 max-w-xl text-base text-white/55">
                                הקלידו מילה או שאלה כדי לחפש כתבות - לפי משמעות וכותרת.
                            </p>
                        ) : null}
                    </div>
                </div>
            </Cell>

            {data.q ? (
                <Cell>
                    {data.articles.length > 0 ? (
                        <SectionArticles
                            title={`${data.articles.length} תוצאות עבור "${data.q}"`}
                            articles={data.articles}
                        />
                    ) : (
                        <div className="px-6 py-16">
                            <p className="text-lg text-white/55">
                                לא נמצאו כתבות מתאימות עבור &quot;{data.q}&quot;.
                            </p>
                        </div>
                    )}
                </Cell>
            ) : null}
        </GridShell>
    );
}
